import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

function formatDate(str) {
  if (!str) return ''
  return new Date(str).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}
function formatCurrency(n) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)
}

export default function Reports() {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [clients, setClients] = useState([])
  const [filterClient, setFilterClient] = useState('all')
  const [copied, setCopied] = useState(false)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => { loadReports(); loadClients() }, [])

  async function loadReports() {
    setLoading(true)
    const { data } = await supabase
      .from('reports').select('*, clients(name)').order('created_at', { ascending: false })
    setReports(data || [])
    setLoading(false)
  }

  async function loadClients() {
    const { data } = await supabase
      .from('clients').select('id, name').eq('active', true).order('name')
    setClients(data || [])
  }

  async function deleteReport(id) {
    if (!window.confirm('Eliminare questo report?')) return
    setDeleting(id)
    await supabase.from('reports').delete().eq('id', id)
    setReports(prev => prev.filter(r => r.id !== id))
    if (selected?.id === id) setSelected(null)
    setDeleting(null)
  }

  const filtered = filterClient === 'all' ? reports : reports.filter(r => r.client_id === filterClient)

  const cardStyle = (active) => ({
    background: active ? 'rgba(124,58,237,0.08)' : 'var(--card-bg)',
    border: '1px solid ' + (active ? 'rgba(124,58,237,0.4)' : 'var(--border)'),
    borderRadius: 10, padding: '14px 16px', cursor: 'pointer', marginBottom: 8, transition: 'all 0.15s',
  })

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'Geist, sans-serif', overflow: 'hidden' }}>
      <div style={{ width: 340, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '24px 20px 16px', borderBottom: '1px solid var(--border)' }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', margin: '0 0 12px', fontFamily: 'DM Serif Display, serif' }}>Report</h1>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', color: 'var(--text)', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
            <option value="all">Tutti i clienti ({reports.length})</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({reports.filter(r => r.client_id === c.id).length})</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 13 }}>Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 13 }}>Nessun report ancora.</div>
          ) : filtered.map(r => (
            <div key={r.id} onClick={() => setSelected(r)} style={cardStyle(selected?.id === r.id)}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4, lineHeight: 1.3 }}>{r.title}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.clients?.name} · {formatDate(r.created_at)}</div>
                {r.meta?.totalSpend > 0 && <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 600 }}>{formatCurrency(r.meta.totalSpend)}</div>}
              </div>
              {r.meta?.platforms && (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {r.meta.platforms.map(p => (
                    <span key={p} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4,
                      background: p === 'google' ? 'rgba(96,165,250,0.15)' : 'rgba(167,139,250,0.15)',
                      color: p === 'google' ? '#60a5fa' : '#a78bfa' }}>{p}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selected ? (
          <>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>{selected.title}</h2>
                <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>📅 {formatDate(selected.date_from)} → {formatDate(selected.date_to)}</span>
                  <span>🏢 {selected.clients?.name}</span>
                  {selected.meta?.totalSpend > 0 && <span>💶 {formatCurrency(selected.meta.totalSpend)}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => { navigator.clipboard.writeText(selected.content); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
                  {copied ? '✓ Copiato' : 'Copia testo'}
                </button>
                <button onClick={() => deleteReport(selected.id)} disabled={deleting === selected.id}
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '7px 14px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>
                  {deleting === selected.id ? '...' : 'Elimina'}
                </button>
              </div>
            </div>
            {selected.meta?.totalSpend > 0 && (
              <div style={{ display: 'flex', gap: 12, padding: '12px 28px', borderBottom: '1px solid var(--border)' }}>
                {[
                  { label: 'Spesa totale', value: formatCurrency(selected.meta.totalSpend) },
                  { label: 'Impressioni',  value: (selected.meta.totalImpressions || 0).toLocaleString('it-IT') },
                  { label: 'Click',        value: (selected.meta.totalClicks || 0).toLocaleString('it-IT') },
                  { label: 'Campagne',     value: (selected.meta.campaigns || []).length },
                ].map(m => (
                  <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', flex: 1 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{m.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{m.value}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              <pre style={{ margin: 0, fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, fontFamily: 'Geist Mono, monospace', whiteSpace: 'pre-wrap', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
                {selected.content}
              </pre>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>
            ← Seleziona un report per visualizzarlo
          </div>
        )}
      </div>
    </div>
  )
}
